import { useMemo, useRef } from "react";
import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
  useScroll,
} from "framer-motion";
import { ArrowDown, ShoppingBag, Languages, BookOpen, Palette, Play } from "lucide-react";
import BookCover from "@/components/BookCover";
import { BOOKS, AMAZON_STORE } from "@/data/books";
import { trackClick } from "@/lib/api";
import { scrollToId } from "@/lib/scroll";

const EASE = [0.16, 1, 0.3, 1];

const LeafSilhouette = ({ className }) => (
  <svg viewBox="0 0 200 260" className={className} aria-hidden="true" fill="currentColor">
    <path d="M100 8C36 60 12 140 30 252c10-60 34-104 70-132-36 28-60 72-70 132C170 210 196 122 100 8z" />
  </svg>
);

export default function Hero() {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [9, -9]), {
    stiffness: 110,
    damping: 16,
  });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-13, 13]), {
    stiffness: 110,
    damping: 16,
  });
  const { scrollY } = useScroll();
  const yFar = useTransform(scrollY, [0, 800], [0, -110]);
  const yNear = useTransform(scrollY, [0, 800], [0, 70]);
  const contentFade = useTransform(scrollY, [0, 480], [1, 0]);

  const particles = useMemo(
    () =>
      Array.from({ length: 18 }, (_, i) => ({
        left: (i * 53 + 11) % 100,
        top: (i * 37 + 7) % 100,
        duration: 5 + (i % 5),
        delay: (i % 7) * 0.6,
        size: 2 + (i % 3),
      })),
    []
  );

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      ref={ref}
      onMouseMove={onMove}
      data-testid="hero-section"
      className="grain relative flex min-h-screen items-center overflow-hidden bg-emerald"
    >
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(1200px 700px at 78% 18%, rgba(212,175,55,0.14), transparent 60%), radial-gradient(900px 600px at 12% 88%, rgba(19,78,61,0.9), transparent 65%), linear-gradient(180deg, #0D3B2E 0%, #082A20 70%, #04170F 100%)",
        }}
      />

      <motion.div style={{ y: yFar }} className="absolute inset-0" aria-hidden="true">
        <LeafSilhouette className="absolute -left-16 top-[8%] h-[46vh] w-auto rotate-[24deg] text-emerald-glow/50 blur-[2px]" />
        <LeafSilhouette className="absolute right-[6%] top-[52%] h-[38vh] w-auto -rotate-[130deg] text-emerald-glow/40 blur-[2px]" />
        <LeafSilhouette className="absolute bottom-[-8%] left-[30%] h-[30vh] w-auto rotate-[70deg] text-emerald-deep/90" />
      </motion.div>

      <motion.div style={{ y: yNear }} className="absolute inset-0" aria-hidden="true">
        <LeafSilhouette className="absolute -right-10 top-[4%] h-[52vh] w-auto rotate-[160deg] text-emerald-deep/80 blur-[1px]" />
        <LeafSilhouette className="absolute bottom-[6%] left-[4%] h-[26vh] w-auto -rotate-[35deg] text-emerald-glow/60" />
      </motion.div>

      {particles.map((p, i) => (
        <motion.span
          key={i}
          aria-hidden="true"
          className="absolute rounded-full bg-gold"
          style={{ left: `${p.left}%`, top: `${p.top}%`, width: p.size, height: p.size }}
          animate={{ y: [0, -36, 0], opacity: [0.08, 0.65, 0.08] }}
          transition={{ duration: p.duration, delay: p.delay, repeat: Infinity, ease: "easeInOut" }}
        />
      ))}

      <motion.div
        style={{ opacity: contentFade }}
        className="relative z-10 mx-auto grid w-full max-w-7xl gap-16 px-4 pb-28 pt-36 sm:px-6 lg:grid-cols-[1.12fr_0.88fr] lg:items-center lg:px-8 lg:pb-36 lg:pt-44"
      >
        <div>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
            className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.3em] text-gold"
            data-testid="hero-eyebrow"
          >
            <span className="h-px w-10 bg-gold/70" aria-hidden="true" />
            Banyan Tree Press · The Panchatantra Tales
          </motion.p>

          <h1
            className="mt-7 font-serif text-4xl font-bold leading-[1.05] tracking-tight text-cream sm:text-5xl lg:text-6xl"
            data-testid="hero-headline"
          >
            <span className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.95, delay: 0.25, ease: EASE }}
              >
                Ancient Wisdom.
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-2">
              <motion.span
                className="block italic text-gold"
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.95, delay: 0.42, ease: EASE }}
              >
                Modern Magic.
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.65, ease: EASE }}
            className="mt-6 max-w-xl text-base leading-relaxed text-cream/75 sm:text-lg"
            data-testid="hero-subhead"
          >
            Timeless moral fables reimagined with stunning digital art for curious
            young minds. Now available in 6 languages across the globe.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => scrollToId("library")}
              data-testid="explore-books-btn"
              className="group flex items-center gap-2.5 rounded-full bg-gold px-7 py-3.5 font-mono text-xs font-semibold uppercase tracking-[0.15em] text-emerald-ink transition-transform duration-300 hover:-translate-y-0.5 hover:shadow-glow"
            >
              Explore the Books
              <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
            </button>
            <a
              href={AMAZON_STORE}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="amazon-hero-btn"
              onClick={() => trackClick("hero-shop-amazon", AMAZON_STORE, "hero")}
              className="flex items-center gap-2.5 rounded-full border border-gold/45 px-7 py-3.5 font-mono text-xs font-semibold uppercase tracking-[0.15em] text-gold transition-colors duration-300 hover:bg-gold/10"
            >
              <ShoppingBag className="h-4 w-4" />
              Shop on Amazon
            </a>
          </motion.div>

          <motion.a
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.92, ease: EASE }}
            href="https://www.youtube.com/channel/UCIOdYZDaejW4qKL9eljLfmA"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-youtube-badge"
            onClick={() => trackClick("hero-youtube", "https://www.youtube.com/channel/UCIOdYZDaejW4qKL9eljLfmA", "hero")}
            className="mt-5 inline-flex w-fit items-center gap-2.5 rounded-full border border-gold/30 bg-emerald-deep/60 px-5 py-2.5 text-sm text-cream/80 transition-all duration-300 hover:border-gold/60 hover:text-gold"
          >
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gold text-emerald-ink">
              <Play className="ml-0.5 h-3 w-3 fill-current" aria-hidden="true" />
            </span>
            Watch the stories on YouTube
          </motion.a>

          <motion.ul
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.95, ease: EASE }}
            className="mt-12 flex flex-wrap gap-x-8 gap-y-4"
            data-testid="hero-trust-badges"
          >
            {[
              { icon: Languages, label: "6 Languages" },
              { icon: BookOpen, label: "Ages 4–11" },
              { icon: Palette, label: "Cinematic Digital Art" },
            ].map(({ icon: Icon, label }) => (
              <li key={label} className="flex items-center gap-2.5 text-sm text-cream/65">
                <span className="flex h-8 w-8 items-center justify-center rounded-full border border-gold/30 text-gold">
                  <Icon className="h-3.5 w-3.5" />
                </span>
                {label}
              </li>
            ))}
          </motion.ul>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.55, ease: EASE }}
          className="relative hidden justify-center lg:flex"
          style={{ perspective: 1400 }}
          data-testid="hero-cover-stage"
        >
          <div
            className="absolute left-1/2 top-1/2 h-[70%] w-[80%] -translate-x-1/2 -translate-y-1/2 rounded-full"
            style={{
              background:
                "radial-gradient(closest-side, rgba(212,175,55,0.28), transparent 75%)",
            }}
            aria-hidden="true"
          />
          <div className="absolute left-[4%] top-[12%] w-52 -rotate-12 opacity-55 blur-[1px] xl:w-56">
            <BookCover book={BOOKS[1]} />
          </div>
          <div className="absolute right-[2%] top-[16%] w-52 rotate-12 opacity-55 blur-[1px] xl:w-56">
            <BookCover book={BOOKS[3]} />
          </div>
          <motion.div
            style={{ rotateX, rotateY }}
            animate={{ y: [0, -12, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            className="relative z-10 w-72 shadow-lift xl:w-80"
            data-testid="hero-featured-cover"
          >
            <BookCover book={BOOKS[0]} />
            <div className="absolute -bottom-5 left-1/2 h-6 w-[70%] -translate-x-1/2 rounded-full bg-black/50 blur-xl" aria-hidden="true" />
          </motion.div>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2"
        aria-hidden="true"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className="flex h-12 w-7 items-start justify-center rounded-full border border-cream/25 p-1.5"
        >
          <span className="h-2 w-1 rounded-full bg-gold" />
        </motion.div>
      </motion.div>
    </section>
  );
}
